/**
 * PhoneVerificationGate
 *
 * Renders children blurred with an unbypassable modal overlay until the visitor
 * has both (a) verified their phone via OTP and (b) completed the lead-detail
 * wizard.  The wizard has four steps:
 *
 *   1. phone        – enter phone number, request OTP
 *   2. otp          – enter the 6-digit verification code
 *   3. details      – first name, last name + email
 *   4. relationship – relationship to the business + marketing consent
 *
 * Steps 1–2 authenticate the customer; steps 3–4 enrich the customer record
 * (persisted to the `customers` table via POST /v1/customers/me/profile).  Only
 * once `details_completed` is true does the gate disappear and the full report
 * become interactive.
 *
 * The modal has no close button, no backdrop-click handler, and Escape key is
 * suppressed while it is open — the user cannot bypass it.
 */
import { Fragment, useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Check, CheckCircle2, Loader2, Mail, ShieldCheck, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../stores/authStore'
import { customerAuthApi } from '../../api/client'
import { APP_NAME, BRAND } from '../../config'
import { InternationalPhoneInput, validatePhone, isSupportedPhoneCountry } from './InternationalPhoneInput'
import { getOTPDeliveryMessage } from '../../utils/otpMessages'
import logoHeader from '../../assets/landing/logo-header.svg'

type Step = 'phone' | 'details' | 'otp' | 'relationship'
const STEPS: Step[] = ['phone', 'details', 'otp', 'relationship']

type Relationship = 'owner' | 'service_provider' | 'other'

interface Props {
  scanId: string
  children: React.ReactNode
}

export function PhoneVerificationGate({ scanId, children }: Props) {
  const { isAuthenticated, customer, loadCustomer, requestOTP, verifyOTP, updateProfile, isLoading } = useAuthStore()
  const { t } = useTranslation('auth')

  // Track whether the initial persisted-session check has completed.
  const [checking, setChecking] = useState(true)
  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [countryCode, setCountryCode] = useState('')
  const [country, setCountry] = useState('')
  const [code, setCode] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [relationship, setRelationship] = useState<Relationship | ''>('')
  const [consent, setConsent] = useState(false)
  const [deliveryChannel, setDeliveryChannel] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  // Resolve any persisted session from localStorage on first render.
  // This is a public page: if the session is dead, stay here and show the
  // register/OTP modal — never hard-redirect to /login.
  useEffect(() => {
    loadCustomer({ skipAuthRedirect: true }).finally(() => setChecking(false))
  }, [loadCustomer])

  // The wizard is "done" only once the details step has been submitted.
  const detailsCompleted = customer?.details_completed === true
  const modalVisible = !checking && (!isAuthenticated || !detailsCompleted)

  // If the visitor is authenticated but still needs to complete the details
  // wizard (fresh OTP verification, or a return visit that never finished),
  // jump them straight to step 2 (details) and prefill any known firstName/lastName/email.
  useEffect(() => {
    if (!checking && isAuthenticated && customer && !customer.details_completed) {
      setStep((s) => (s === 'phone' ? 'details' : s))
      setFirstName((n) => n || customer.first_name || '')
      setLastName((n) => n || customer.last_name || '')
      setEmail((e) => e || customer.email || '')
    }
  }, [checking, isAuthenticated, customer])

  // Whenever the user becomes authenticated, attach this scan to their account
  // so the admin dashboard can see the association.  Best-effort.
  const attachedRef = useRef(false)
  useEffect(() => {
    if (!checking && isAuthenticated && scanId && !attachedRef.current) {
      attachedRef.current = true
      customerAuthApi.attachScan(scanId).catch(() => { /* best-effort */ })
    }
  }, [checking, isAuthenticated, scanId])

  // Lock body scroll and suppress Escape while the modal is visible.
  useEffect(() => {
    if (!modalVisible) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') e.preventDefault() }
    window.addEventListener('keydown', onKey, { capture: true })
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey, { capture: true })
    }
  }, [modalVisible])

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate phone number
    if (!validatePhone(phone)) {
      setError(t('login.error.invalidPhone', 'Please enter a valid phone number'))
      return
    }

    // The country dropdown is restricted, but a phone number can still be typed/pasted
    // for an unsupported country. Reject it client-side (UX only — the backend is the
    // source of truth and re-validates country_code independently).
    if (!isSupportedPhoneCountry(phone)) {
      setError(t('login.error.unsupportedCountry', 'This country is not supported yet. Please use a United States, Jordan, or Kuwait phone number.'))
      return
    }

    try {
      // Request OTP. The response indicates whether this is a new or existing customer.
      // - New customer: is_new_customer=true, OTP not sent yet (awaiting details)
      // - Existing customer: is_new_customer=false, OTP sent immediately
      const result = await requestOTP(phone, countryCode, country, false)

      setDeliveryChannel(result.delivery_channel)

      if (result.is_new_customer) {
        // New customer: collect personal information before verifying OTP
        setStep('details')
      } else {
        // Existing customer: skip details, go straight to OTP verification
        setStep('otp')
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? t('login.error.default'))
    }
  }

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await verifyOTP(phone, code, countryCode, country)
      // Authenticated now, but the details wizard is still pending — advance to
      // step 4.  The gate stays visible because `details_completed` is false.
      setStep('relationship')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? t('login.error.default'))
    }
  }

  const handleSubmitDetails = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!firstName.trim()) { setError(t('details.error.firstName', 'First name is required')); return }
    if (!lastName.trim()) { setError(t('details.error.lastName', 'Last name is required')); return }
    if (!email.trim()) { setError(t('details.error.email')); return }

    try {
      // Now that we have personal information, persist it to the customer record
      // via the backend by calling requestOTP again. This time, the service will
      // have firstName/lastName/email and can send the OTP successfully.
      await requestOTP(phone, countryCode, country, false, firstName, lastName.trim(), email)
      // OTP requested successfully — move to OTP verification step
      setStep('otp')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? t('login.error.default'))
    }
  }

  const handleSubmitRelationship = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!relationship) { setError(t('relationship.error.required')); return }
    try {
      await updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        relationship,
        marketing_consent: consent,
      })
      // `customer.details_completed` is now true → the gate disappears.
    } catch (err: any) {
      setError(err?.response?.data?.error ?? t('login.error.default'))
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  // Still resolving persisted session → show blurred content + loading spinner.
  if (checking) {
    return (
      <div className="relative">
        <div aria-hidden="true" style={{ filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }}>
          {children}
        </div>
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-white/50 backdrop-blur-sm">
          <Loader2 className="w-7 h-7 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  // Fully verified and details complete → render children directly with no gate.
  if (!modalVisible) {
    return <>{children}</>
  }

  const currentIndex = STEPS.indexOf(step)

  // Not done → blurred children + unbypassable modal.
  return (
    <div className="relative">
      {/* Non-interactive blurred background */}
      <div
        aria-hidden="true"
        style={{ filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
      >
        {children}
      </div>

      {/* Overlay — no onClick so backdrop clicks do nothing */}
      <div
        className="fixed inset-0 z-[2000] overflow-y-auto"
        style={{ backgroundColor: 'rgba(3,17,47,0.72)' }}
        // Intentionally no onClick handler — backdrop must not close the modal.
      >
        <div className="flex min-h-full items-center justify-center p-4 py-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="px-6 pt-6 pb-5 text-center border-b border-gray-100">
              <div className="flex justify-center mb-4">
                <img src={logoHeader} alt={APP_NAME} className="h-10 w-auto" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1.5">
                {t('gate.title')}
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                {t('gate.subtitle')}
              </p>

              {/* Benefits list */}
              <div className="mt-4 space-y-2 text-left">
                {(['save', 'recommendations', 'rescan'] as const).map((key) => (
                  <div key={key} className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: BRAND.red }} />
                    <span className="text-sm text-gray-600">{t(`gate.benefit.${key}`)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Form ────────────────────────────────────────────────────── */}
            <div className="px-6 py-5">
              {/* Step indicators (1 → 2 → 3 → 4) */}
              <div className="flex items-center gap-1.5 mb-5">
                {STEPS.map((s, i) => {
                  const done = i < currentIndex
                  const active = i <= currentIndex
                  return (
                    <Fragment key={s}>
                      {i > 0 && <div className="flex-1 h-px bg-gray-200" />}
                      <div
                        className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition-colors ${
                          active ? 'text-white' : 'text-gray-400 border border-gray-200'
                        }`}
                        style={active ? { backgroundColor: BRAND.red } : {}}
                      >
                        {done ? <Check className="w-3.5 h-3.5" /> : s === 'otp' && currentIndex >= 1 ? <ShieldCheck className="w-3.5 h-3.5" /> : i + 1}
                      </div>
                    </Fragment>
                  )
                })}
              </div>

              <AnimatePresence mode="wait">
                {/* ── Step 1: phone ───────────────────────────────────────── */}
                {step === 'phone' && (
                  <motion.form
                    key="phone"
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 12 }}
                    onSubmit={handleRequestOTP}
                    className="space-y-4"
                  >
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-0.5">
                        {t('login.phoneTitle', 'Enter your phone number')}
                      </p>
                      <p className="text-xs text-gray-500 mb-3">
                        {t('login.phoneSubtitle', "We'll send you a verification code")}
                      </p>

                      {error && (
                        <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-3 text-sm text-red-700">
                          {error}
                        </div>
                      )}

                      <InternationalPhoneInput
                        value={phone}
                        onChange={setPhone}
                        onCountryChange={(code, name) => {
                          setCountryCode(code)
                          setCountry(name)
                        }}
                        placeholder={t('login.phonePlaceholder', '+1 555 000 0000')}
                        autoFocus
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading || !phone}
                      className="w-full disabled:bg-gray-200 text-white font-medium py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                      style={{ backgroundColor: isLoading || !phone ? undefined : BRAND.red }}
                      onMouseOver={(e) => { if (!isLoading && phone) (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND.redHover }}
                      onMouseOut={(e) => { if (!isLoading && phone) (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND.red }}
                    >
                      {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {t('login.sendCode', 'Send Verification Code')}
                    </button>
                  </motion.form>
                )}

                {/* ── Step 3: otp ─────────────────────────────────────────── */}
                {step === 'otp' && (
                  <motion.form
                    key="otp"
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    onSubmit={handleVerifyOTP}
                    className="space-y-4"
                  >
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-0.5">
                        {t('login.otpTitle', 'Enter verification code')}
                      </p>
                      <p className="text-xs text-gray-500 mb-3">
                        {getOTPDeliveryMessage(deliveryChannel, phone)}
                      </p>

                      {error && (
                        <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-3 text-sm text-red-700">
                          {error}
                        </div>
                      )}

                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                        required
                        autoFocus
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 transition-colors text-center tracking-[0.4em] font-mono text-lg"
                        style={{ '--tw-ring-color': BRAND.red } as React.CSSProperties}
                        placeholder="••••••"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading || code.length < 6}
                      className="w-full disabled:bg-gray-200 text-white font-medium py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                      style={{ backgroundColor: isLoading || code.length < 6 ? undefined : BRAND.red }}
                      onMouseOver={(e) => { if (!isLoading && code.length >= 6) (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND.redHover }}
                      onMouseOut={(e) => { if (!isLoading && code.length >= 6) (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND.red }}
                    >
                      {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {t('login.verify', 'Verify & Access Report')}
                    </button>

                    <div className="flex flex-col gap-1 pt-1">
                      <button
                        type="button"
                        onClick={() => { setStep('phone'); setCode(''); setError(null) }}
                        className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        {t('login.changePhone', '← Change phone number')}
                      </button>
                      <button
                        type="button"
                        disabled={isLoading}
                        onClick={async () => {
                          setError(null)
                          try { await requestOTP(phone, countryCode, country) } catch (err: any) {
                            setError(err?.response?.data?.error ?? t('login.error.default'))
                          }
                        }}
                        className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {t('login.resendCode', 'Resend code')}
                      </button>
                    </div>
                  </motion.form>
                )}

                {/* ── Step 2: details (first name, last name + email) ──────────────────────── */}
                {step === 'details' && (
                  <motion.form
                    key="details"
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    onSubmit={handleSubmitDetails}
                    className="space-y-4"
                  >
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-0.5">
                        {t('details.title', 'Tell us about you')}
                      </p>
                      <p className="text-xs text-gray-500 mb-3">
                        {t('details.subtitle', 'We use this to personalise your report.')}
                      </p>

                      {error && (
                        <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-3 text-sm text-red-700">
                          {error}
                        </div>
                      )}

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('details.firstName', 'First Name')}
                          </label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              required
                              autoFocus
                              className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 transition-colors"
                              style={{ '--tw-ring-color': BRAND.red } as React.CSSProperties}
                              placeholder={t('details.firstNamePlaceholder', 'Your first name')}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('details.lastName', 'Last Name')}
                          </label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              required
                              className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 transition-colors"
                              style={{ '--tw-ring-color': BRAND.red } as React.CSSProperties}
                              placeholder={t('details.lastNamePlaceholder', 'Your last name')}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('details.email', 'Email')}
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              required
                              className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 transition-colors"
                              style={{ '--tw-ring-color': BRAND.red } as React.CSSProperties}
                              placeholder={t('details.emailPlaceholder', 'Your email')}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading || !firstName.trim() || !email.trim()}
                      className="w-full disabled:bg-gray-200 text-white font-medium py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                      style={{ backgroundColor: isLoading || !firstName.trim() || !email.trim() ? undefined : BRAND.red }}
                      onMouseOver={(e) => { if (!isLoading && firstName.trim() && email.trim()) (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND.redHover }}
                      onMouseOut={(e) => { if (!isLoading && firstName.trim() && email.trim()) (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND.red }}
                    >
                      {t('details.continue', 'Continue')}
                    </button>
                  </motion.form>
                )}

                {/* ── Step 4: relationship + consent ──────────────────── */}
                {step === 'relationship' && (
                  <motion.form
                    key="relationship"
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    onSubmit={handleSubmitRelationship}
                    className="space-y-4"
                  >
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-3">
                        {t('relationship.question', "What's your relationship to this business?")}
                      </p>

                      {error && (
                        <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-3 text-sm text-red-700">
                          {error}
                        </div>
                      )}

                      <div className="space-y-2">
                        {([
                          ['owner', t('relationship.owner', "I'm the business owner or manager")],
                          ['service_provider', t('relationship.serviceProvider', 'I provide services to this business')],
                          ['other', t('relationship.other', 'Other')],
                        ] as const).map(([value, label]) => {
                          const selected = relationship === value
                          return (
                            <label
                              key={value}
                              className="flex items-center gap-3 border rounded-xl px-3.5 py-3 cursor-pointer transition-colors"
                              style={{ borderColor: selected ? BRAND.red : '#e5e7eb', backgroundColor: selected ? 'rgba(220,38,38,0.04)' : undefined }}
                            >
                              <input
                                type="radio"
                                name="relationship"
                                value={value}
                                checked={selected}
                                onChange={() => setRelationship(value)}
                                className="w-4 h-4"
                                style={{ accentColor: BRAND.red }}
                              />
                              <span className="text-sm text-gray-800">{label}</span>
                            </label>
                          )
                        })}
                      </div>

                      {/* Marketing consent */}
                      <label className="flex items-start gap-2.5 bg-gray-50 rounded-xl p-3.5 mt-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={consent}
                          onChange={(e) => setConsent(e.target.checked)}
                          className="w-4 h-4 mt-0.5 shrink-0"
                          style={{ accentColor: BRAND.red }}
                        />
                        <span>
                          <span className="block text-sm font-semibold text-gray-900">
                            {t('relationship.consent', 'Receive updates on your score and competitors')}
                          </span>
                          <span className="block text-xs text-gray-500 mt-1 leading-relaxed">
                            {t('relationship.consentText', 'By checking this box, I agree to receive automated marketing messages at the phone number provided. Consent is not required to receive the report. Message frequency varies. Message and data rates may apply. Reply STOP to cancel anytime.')}
                          </span>
                        </span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading || !relationship}
                      className="w-full disabled:bg-gray-200 text-white font-medium py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                      style={{ backgroundColor: isLoading || !relationship ? undefined : BRAND.red }}
                      onMouseOver={(e) => { if (!isLoading && relationship) (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND.redHover }}
                      onMouseOut={(e) => { if (!isLoading && relationship) (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND.red }}
                    >
                      {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {t('relationship.submit', 'Unlock Report')}
                    </button>

                    <button
                      type="button"
                      onClick={() => { setStep('details'); setError(null) }}
                      className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {t('relationship.back', '← Back')}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
